import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
  type FetchQueryOptions,
  type MutationFunction,
  type MutationKey,
  type QueryClientConfig,
  type QueryFunction,
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  Store,
  createStore,
  type StoreActionMap,
  type StoreActionsFactory,
} from '@tanstack/store';
import { useStore as useTanStackStore } from '@tanstack/react-store';
import {
  createCollection,
  eq as tovDbEq,
  localOnlyCollectionOptions,
  type Collection,
} from '@tanstack/db';
import { useLiveQuery as useTovLiveQuery } from '@tanstack/react-db';
import {
  AsyncDebouncer,
  type AsyncDebouncerOptions,
  asyncBatch,
  type AsyncBatcherOptions,
  asyncRetry,
  type AsyncRetryerOptions,
  type AnyAsyncFunction,
} from '@tanstack/pacer';
import { useState, type ReactNode } from 'react';

export * from './tovRouter';

export type TovFrontendAppName = 'dash' | 'admin' | 'vendors' | 'guests';

export type TovQueryKey = readonly [
  TovFrontendAppName,
  string,
  string,
  ...ReadonlyArray<unknown>,
];

export const tovDefaultNetworkRetryDelaysMs = [250, 500, 1_000, 2_000, 4_000];

export function createTovQueryKey(
  appName: TovFrontendAppName,
  scope: string,
  resource: string,
  ...parts: ReadonlyArray<unknown>
): TovQueryKey {
  return [appName, scope, resource, ...parts];
}

export function createTovGlobalQueryKey(
  appName: TovFrontendAppName,
  resource: string,
  ...parts: ReadonlyArray<unknown>
): TovQueryKey {
  return createTovQueryKey(appName, 'global', resource, ...parts);
}

export function createTovOccasionQueryKey(
  appName: TovFrontendAppName,
  occasionId: string,
  resource: string,
  ...parts: ReadonlyArray<unknown>
): TovQueryKey {
  return createTovQueryKey(
    appName,
    `occasion:${occasionId}`,
    resource,
    ...parts,
  );
}

export function createTovGuestQueryKey(
  occasionSlug: string,
  guestToken: string,
  resource: string,
  ...parts: ReadonlyArray<unknown>
): TovQueryKey {
  return createTovQueryKey(
    'guests',
    `guest:${occasionSlug}:${guestToken}`,
    resource,
    ...parts,
  );
}

export function createTovNetworkRetry(
  isRetriableFailure: (error: unknown) => boolean,
  maxRetries = tovDefaultNetworkRetryDelaysMs.length,
) {
  return (failureCount: number, error: unknown) =>
    failureCount < maxRetries && isRetriableFailure(error);
}

export function createTovRetryDelay(
  retryDelaysMs: readonly number[] = tovDefaultNetworkRetryDelaysMs,
) {
  return (attemptIndex: number) =>
    retryDelaysMs[Math.min(attemptIndex, retryDelaysMs.length - 1)] ?? 0;
}

export interface TovAsyncBatcherOptions<TValue> extends Omit<
  AsyncBatcherOptions<TValue>,
  'maxSize' | 'wait'
> {
  maxSize?: number;
  waitMs?: number;
}

export function createTovAsyncBatcher<TValue, TResult>(
  fn: (items: TValue[]) => Promise<TResult>,
  options: TovAsyncBatcherOptions<TValue> = {},
) {
  return asyncBatch<TValue>(fn, {
    ...options,
    maxSize: options.maxSize ?? 25,
    wait: options.waitMs ?? 0,
  });
}

export interface TovAsyncDebouncer<TFn extends AnyAsyncFunction> {
  abort(): void;
  cancel(): void;
  maybeExecute(
    ...args: Parameters<TFn>
  ): Promise<Awaited<ReturnType<TFn>> | undefined>;
}

export interface TovAsyncDebouncerOptions<
  TFn extends AnyAsyncFunction,
> extends Omit<AsyncDebouncerOptions<TFn>, 'wait'> {
  waitMs?: number;
}

export function createTovAsyncDebouncer<TFn extends AnyAsyncFunction>(
  fn: TFn,
  options: TovAsyncDebouncerOptions<TFn> = {},
): TovAsyncDebouncer<TFn> {
  return new AsyncDebouncer(fn, {
    ...options,
    wait: options.waitMs ?? 250,
  });
}

export interface TovAsyncRetryOptions<
  TFn extends AnyAsyncFunction,
> extends Omit<AsyncRetryerOptions<TFn>, 'maxAttempts' | 'baseWait'> {
  baseWaitMs?: number;
  maxAttempts?: number;
}

export function createTovAsyncRetry<TFn extends AnyAsyncFunction>(
  fn: TFn,
  options: TovAsyncRetryOptions<TFn> = {},
) {
  return asyncRetry(fn, {
    ...options,
    baseWait: options.baseWaitMs ?? 250,
    maxAttempts: options.maxAttempts ?? 3,
  });
}

export function createTovQueryClient(
  appName: TovFrontendAppName,
  config?: QueryClientConfig,
) {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: 0,
        ...config?.defaultOptions?.queries,
        meta: {
          appName,
          ...config?.defaultOptions?.queries?.meta,
        },
      },
      mutations: {
        retry: false,
        ...config?.defaultOptions?.mutations,
        meta: {
          appName,
          ...config?.defaultOptions?.mutations?.meta,
        },
      },
    },
    ...config,
  });
}

export function TovQueryProvider({
  appName,
  children,
  client,
}: {
  appName: TovFrontendAppName;
  children: ReactNode;
  client?: QueryClient;
}) {
  const [queryClient] = useState(() => client ?? createTovQueryClient(appName));

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}

export async function invalidateQueryGroups(
  queryClient: QueryClient,
  queryKeys: readonly QueryKey[],
) {
  await Promise.all(
    queryKeys.map((queryKey) =>
      queryClient.invalidateQueries({
        queryKey,
      }),
    ),
  );
}

export async function invalidateTovQuery(
  queryClient: QueryClient,
  queryKey: QueryKey,
) {
  await queryClient.invalidateQueries({
    queryKey,
  });
}

export function setTovQueryData<TData>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  updater: TData | ((current: TData | undefined) => TData | undefined),
) {
  return queryClient.setQueryData<TData>(queryKey, updater);
}

export interface FetchTovQueryOptions<
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends TovQueryKey,
> extends Omit<
  FetchQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  'queryKey' | 'queryFn'
> {
  queryKey: TQueryKey;
  queryFn: QueryFunction<TQueryFnData, TQueryKey>;
}

export function fetchTovQuery<
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends TovQueryKey = TovQueryKey,
>(
  queryClient: QueryClient,
  options: FetchTovQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
) {
  return queryClient.fetchQuery<TQueryFnData, TError, TData, TQueryKey>(
    options,
  );
}

export function preloadTovRouteQuery<
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends TovQueryKey = TovQueryKey,
>(
  queryClient: QueryClient,
  options: FetchTovQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
) {
  return fetchTovQuery(queryClient, options);
}

export const useTovQueryClient = useQueryClient;

export interface UseTovQueryOptions<
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends TovQueryKey,
> extends Omit<
  UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  'queryKey' | 'queryFn'
> {
  queryKey: TQueryKey;
  queryFn: QueryFunction<TQueryFnData, TQueryKey>;
}

export function useTovQuery<
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends TovQueryKey = TovQueryKey,
>(
  options: UseTovQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
): UseQueryResult<TData, TError> {
  return useQuery<TQueryFnData, TError, TData, TQueryKey>(options);
}

export interface UseTovMutationOptions<
  TData,
  TError,
  TVariables,
  TContext,
> extends Omit<
  UseMutationOptions<TData, TError, TVariables, TContext>,
  'mutationFn' | 'onSuccess'
> {
  mutationFn: MutationFunction<TData, TVariables>;
  invalidate?:
    | readonly QueryKey[]
    | ((
        data: TData,
        variables: TVariables,
        context: TContext,
      ) => readonly QueryKey[] | Promise<readonly QueryKey[]>);
  onSuccess?: (
    data: TData,
    variables: TVariables,
    context: TContext,
  ) => void | Promise<void>;
  mutationKey?: MutationKey;
}

export function useTovMutation<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown,
>(
  options: UseTovMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const queryClient = useQueryClient();

  return useMutation<TData, TError, TVariables, TContext>({
    ...options,
    mutationFn: options.mutationFn,
    async onSuccess(data, variables, context) {
      await options.onSuccess?.(data, variables, context);
      const queryKeys =
        typeof options.invalidate === 'function'
          ? await options.invalidate(data, variables, context)
          : options.invalidate;

      if (queryKeys && queryKeys.length > 0) {
        await invalidateQueryGroups(queryClient, queryKeys);
      }
    },
  });
}

export type TovSearchParamField<TValue> = {
  defaultValue: TValue;
  parse(value: string | null): TValue;
  serialize(value: TValue): string | null;
};

export type TovSearchParamSchema = Record<string, TovSearchParamField<unknown>>;

export type TovSearchParamValues<TSchema extends TovSearchParamSchema> = {
  [Key in keyof TSchema]: TSchema[Key] extends TovSearchParamField<infer TValue>
    ? TValue
    : never;
};

export function tovStringParam(
  options: {
    defaultValue?: string;
    trim?: boolean;
  } = {},
): TovSearchParamField<string> {
  const defaultValue = options.defaultValue ?? '';

  return {
    defaultValue,
    parse(value) {
      const nextValue = value ?? defaultValue;
      return options.trim === false ? nextValue : nextValue.trim();
    },
    serialize(value) {
      const nextValue = options.trim === false ? value : value.trim();
      return nextValue === defaultValue || nextValue.length === 0
        ? null
        : nextValue;
    },
  };
}

export function tovEnumParam<const TValue extends string>(
  allowedValues: readonly TValue[],
  defaultValue: TValue,
): TovSearchParamField<TValue> {
  return {
    defaultValue,
    parse(value) {
      return allowedValues.includes(value as TValue)
        ? (value as TValue)
        : defaultValue;
    },
    serialize(value) {
      return value === defaultValue ? null : value;
    },
  };
}

export function tovPositiveIntParam(options: {
  defaultValue: number;
  max?: number;
  min?: number;
}): TovSearchParamField<number> {
  const min = options.min ?? 1;

  return {
    defaultValue: options.defaultValue,
    parse(value) {
      const parsed = Number(value);
      if (!Number.isInteger(parsed) || parsed < min) {
        return options.defaultValue;
      }

      return options.max ? Math.min(parsed, options.max) : parsed;
    },
    serialize(value) {
      const normalized = Math.max(
        min,
        options.max ? Math.min(value, options.max) : value,
      );
      return normalized === options.defaultValue ? null : String(normalized);
    },
  };
}

export function readTovSearchParams<TSchema extends TovSearchParamSchema>(
  searchParams: URLSearchParams,
  schema: TSchema,
): TovSearchParamValues<TSchema> {
  return Object.fromEntries(
    Object.entries(schema).map(([key, field]) => [
      key,
      field.parse(searchParams.get(key)),
    ]),
  ) as TovSearchParamValues<TSchema>;
}

export function writeTovSearchParams<TSchema extends TovSearchParamSchema>(
  searchParams: URLSearchParams,
  schema: TSchema,
  values: Partial<TovSearchParamValues<TSchema>>,
) {
  const next = new URLSearchParams(searchParams);

  Object.entries(values).forEach(([key, value]) => {
    const field = schema[key];
    if (!field) {
      return;
    }

    const serialized = field.serialize(value);
    if (serialized === null) {
      next.delete(key);
    } else {
      next.set(key, serialized);
    }
  });

  return next;
}

export interface TovJsonStorageOptions<TValue> {
  storage?: 'local' | 'session';
  validate?: (value: unknown) => value is TValue;
}

function getTovStorage(storage: 'local' | 'session') {
  if (typeof window === 'undefined') {
    return null;
  }

  return storage === 'session' ? window.sessionStorage : window.localStorage;
}

export function readTovStoredJson<TValue>(
  key: string,
  fallback: TValue,
  options: TovJsonStorageOptions<TValue> = {},
) {
  const storage = getTovStorage(options.storage ?? 'local');
  if (!storage) {
    return fallback;
  }

  try {
    const raw = storage.getItem(key);
    if (!raw) {
      return fallback;
    }

    const parsed = JSON.parse(raw) as unknown;
    if (options.validate && !options.validate(parsed)) {
      return fallback;
    }

    return parsed as TValue;
  } catch {
    return fallback;
  }
}

export function writeTovStoredJson<TValue>(
  key: string,
  value: TValue,
  options: Pick<TovJsonStorageOptions<TValue>, 'storage'> = {},
) {
  const storage = getTovStorage(options.storage ?? 'local');
  if (!storage) {
    return;
  }

  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore storage quota and privacy-mode failures
  }
}

export { Store, createStore };
export type { StoreActionMap, StoreActionsFactory };

export function useTovStore<TState, TSelected>(
  store: {
    get: () => TState;
    subscribe: (listener: (value: TState) => void) => {
      unsubscribe: () => void;
    };
  },
  selector: (state: TState) => TSelected,
  compare?: (left: TSelected, right: TSelected) => boolean,
) {
  return useTanStackStore(store, selector, compare);
}

export interface TovLocalCollectionOptions<
  TItem extends object,
  TKey extends string | number,
> {
  id: string;
  initialData?: TItem[];
  getKey(item: TItem): TKey;
}

export function createTovLocalCollection<
  TItem extends object,
  TKey extends string | number,
>(options: TovLocalCollectionOptions<TItem, TKey>) {
  return createCollection(
    localOnlyCollectionOptions<TItem, TKey>({
      id: options.id,
      getKey: options.getKey,
      initialData: options.initialData,
    }),
  );
}

export { tovDbEq, useTovLiveQuery };
export type { Collection };
